// src/features/chat/components/his/FutureActionCommitmentCard.jsx
/*
 * A future-action commitment is visible proof of accepted scheduled work.
 * It is not a task manager row: v1 exposes no direct cancel, reschedule, or
 * replace controls.
 */

import React from 'react';
import { CalendarClock, Check, X } from 'lucide-react';
import styles from '../../styles/HisArtifact.module.css';
import Collapse from './Collapse';
import ArtifactMetaLine from './ArtifactMetaLine';
import {
  futureActionCommitmentPromise,
  futureActionCommitmentScheduledForText,
  futureActionCommitmentTitle,
  futureActionCommitmentUnavailableText,
  futureActionCommitmentWhisperText,
  selectFutureActionCommitmentDisplayState,
} from '../../templates/futureActionCommitmentTemplates';

function SettlementGlyph({ outcome }) {
  if (outcome === 'fulfilled') {
    return (
      <span className={styles.whisperGlyphOk} aria-hidden="true">
        <Check size={13} strokeWidth={2.4} />
      </span>
    );
  }
  if (outcome === 'cancelled') {
    return (
      <span className={styles.whisperGlyphMuted} aria-hidden="true">
        <X size={13} strokeWidth={2.2} />
      </span>
    );
  }
  return (
    <span className={styles.whisperGlyphMuted} aria-hidden="true">
      ·
    </span>
  );
}

export function FutureActionCommitmentCard({
  commitment,
  born = false,
  showMeta = true,
  botName,
  timeMs = 0,
}) {
  const displayState = selectFutureActionCommitmentDisplayState(commitment);
  const unavailable = displayState.kind === 'unavailable';

  React.useEffect(() => {
    if (!unavailable) return;
    console.error('Future-action commitment could not be shown.', {
      commitmentId: commitment?.id || null,
    });
  }, [unavailable, commitment?.id]);

  if (unavailable) {
    return (
      <div
        className={[styles.card, born ? styles.rise : ''].filter(Boolean).join(' ')}
        data-kind="commitment"
        data-settled="true"
      >
        <Collapse open>
          <div className={styles.whisper} data-quiet="true">
            <SettlementGlyph outcome="unavailable" />
            <span>{futureActionCommitmentUnavailableText()}</span>
          </div>
        </Collapse>
      </div>
    );
  }

  const settled = displayState.kind === 'settled';

  return (
    <div
      className={[styles.card, born ? styles.rise : ''].filter(Boolean).join(' ')}
      data-kind="commitment"
      data-settled={settled ? 'true' : 'false'}
    >
      {!settled && (
        <Collapse open>
          <div>
            {showMeta && <ArtifactMetaLine botName={botName} timeMs={timeMs} />}
            <div className={styles.header}>
              <span className={styles.headerIcon}>
                <CalendarClock size={15} aria-hidden="true" />
              </span>
              <span className={styles.title}>{futureActionCommitmentTitle()}</span>
            </div>
            <div className={styles.body}>
              <dl className={styles.fieldList}>
                <div className={styles.fieldRow}>
                  <dt>Affin will</dt>
                  <dd>{futureActionCommitmentPromise(displayState)}</dd>
                </div>
                <div className={styles.fieldRow}>
                  <dt>Scheduled</dt>
                  <dd>{futureActionCommitmentScheduledForText(displayState)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </Collapse>
      )}

      {settled && (
        <Collapse open>
          <div
            className={styles.whisper}
            data-quiet={displayState.outcome === 'fulfilled' ? 'false' : 'true'}
          >
            <SettlementGlyph outcome={displayState.outcome} />
            <span>{futureActionCommitmentWhisperText(displayState)}</span>
          </div>
        </Collapse>
      )}
    </div>
  );
}

export default FutureActionCommitmentCard;
